// Tenantra Inc. - Submit Report Page
import { useState, useEffect } from 'react';
import { Loader2, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { navigate } from '@/services/navigation';
import api, { apiHelpers } from '@/services/api';
import { track } from '@/services/analytics';
import SubscriptionGate from '@/components/SubscriptionGate';

const PAYMENT_STATUSES = [
  { value: 'on_time', label: 'On time' },
  { value: 'late', label: 'Late' },
  { value: 'partial', label: 'Partial' },
  { value: 'missed', label: 'Missed' }
];

const LEASE_STATUSES = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'broken', label: 'Broken' }
];

const ISSUE_TYPES = [
  { value: 'damage', label: 'Property Damage' },
  { value: 'noise', label: 'Noise/Disturbance' },
  { value: 'unauthorized_occupant', label: 'Unauthorized Occupant' },
  { value: 'other', label: 'Other' }
];

const ISSUE_SEVERITIES = [
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' }
];

const tenancyIdFromPath = () => {
  const m = window.location.pathname.match(/^\/landlord\/reports\/([^/]+)\/new$/);
  return m ? m[1] : '';
};

const Section = ({ title, children, action }) => (
  <section className="bg-white border border-slate-200 rounded-lg">
    <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between">
      <h2 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{title}</h2>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

export default function SubmitReportPage() {
  const [loading, setLoading] = useState(false);
  const [loadingTenancies, setLoadingTenancies] = useState(true);
  const [tenancies, setTenancies] = useState([]);
  const [errors, setErrors] = useState({});
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [formData, setFormData] = useState({
    tenancy_id: tenancyIdFromPath(),
    period_start: '',
    period_end: '',
    payment_status: 'on_time',
    days_late: '',
    amount_owing: '',
    payment_notes: '',
    lease_status: 'in_progress',
    early_termination: false,
    notice_given: true,
    issues: [],
    notes: ''
  });

  useEffect(() => {
    fetchTenancies();
  }, []);

  const fetchTenancies = async () => {
    try {
      setSubscriptionRequired(false);
      const response = await api.get('/tenancies?status=active');
      setTenancies(response.data.items || []);
    } catch (err) {
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
        setSubscriptionMessage(err.response?.data?.detail?.message || 'An active subscription is required to submit reports.');
        return;
      }
      toast.error('Failed to load tenancies');
    } finally {
      setLoadingTenancies(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addIssue = () => {
    setFormData(prev => ({
      ...prev,
      issues: [...prev.issues, { type: 'damage', description: '', severity: 'minor' }]
    }));
  };

  const updateIssue = (index, field, value) => {
    setFormData(prev => {
      const newIssues = [...prev.issues];
      newIssues[index] = { ...newIssues[index], [field]: value };
      return { ...prev, issues: newIssues };
    });
  };

  const removeIssue = (index) => {
    setFormData(prev => ({
      ...prev,
      issues: prev.issues.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.tenancy_id) {
      newErrors.tenancy_id = 'Please select a tenancy';
    }
    if (!formData.period_start) {
      newErrors.period_start = 'Start date is required';
    }
    if (!formData.period_end) {
      newErrors.period_end = 'End date is required';
    }
    if (formData.period_start && formData.period_end && 
        new Date(formData.period_start) > new Date(formData.period_end)) {
      newErrors.period_end = 'End date must be after start date';
    }
    if ((formData.payment_status === 'late' || formData.payment_status === 'partial') && 
        !formData.days_late) {
      newErrors.days_late = 'Please specify days late';
    }
    
    // Validate issues
    formData.issues.forEach((issue, i) => {
      if (!issue.description || issue.description.length < 10) {
        newErrors[`issue_${i}`] = 'Issue description must be at least 10 characters';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Check the highlighted fields');
      return;
    }

    try {
      setLoading(true);
      await api.post('/reports', {
        tenancy_id: formData.tenancy_id,
        report_period: {
          start: formData.period_start,
          end: formData.period_end
        },
        payment: {
          status: formData.payment_status,
          days_late: formData.days_late ? parseInt(formData.days_late) : null,
          amount_owing: formData.amount_owing ? parseFloat(formData.amount_owing) : null,
          notes: formData.payment_notes || null
        },
        lease: {
          status: formData.lease_status,
          early_termination: formData.early_termination,
          notice_given: formData.notice_given
        },
        issues: formData.issues.map(i => ({
          type: i.type,
          description: i.description,
          severity: i.severity
        })),
        notes: formData.notes || null,
        evidence_file_ids: []
      });
      
      track('report_filed');
      toast.success('Report filed');
      navigate('/landlord/reports');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const selectedTenancy = tenancies.find(t => t.id === formData.tenancy_id);
  const inputCls = 'h-9 bg-white border-slate-300 text-slate-900 text-sm focus:border-teal-600 focus:ring-teal-600/20';
  const selectCls = 'w-full h-9 px-3 pr-8 bg-white border border-slate-300 rounded-md text-slate-900 text-sm focus:outline-none focus:ring-1 focus:ring-teal-600 appearance-none';
  const err = (k) => errors[k] ? <p className="text-red-600 text-xs mt-1" data-testid={`error-${k}`}>{errors[k]}</p> : null;

  if (subscriptionRequired) {
    return <SubscriptionGate message={subscriptionMessage} />;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="submit-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-semibold text-slate-900">File a report</h1>
          <p className="text-xs text-slate-500 mt-0.5">One reporting period for one tenancy. The tenant can read everything you enter.</p>
        </div>
        <a href="/landlord/reports" className="text-xs text-slate-500 hover:text-slate-800">Back to reports</a>
      </div>

      {!loadingTenancies && tenancies.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3" data-testid="no-tenancies-notice">
          <p className="text-sm text-slate-700">You need an active tenancy before you can file a report.</p>
          <a href="/landlord/tenants/new" className="text-sm font-medium text-teal-700 hover:text-teal-800">Add tenancy</a>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Section title="Tenancy">
          {loadingTenancies ? (
            <div className="h-9 bg-slate-100 rounded-md animate-pulse" />
          ) : (
            <>
              <div className="relative">
                <select
                  value={formData.tenancy_id}
                  onChange={(e) => handleChange('tenancy_id', e.target.value)}
                  className={`${selectCls} ${errors.tenancy_id ? 'border-red-400' : ''}`}
                  data-testid="report-tenancy-select"
                >
                  <option value="">Select a tenancy</option>
                  {tenancies.map(t => (
                    <option key={t.id} value={t.id}>{t.tenant_name} — {t.property_address}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              {err("tenancy_id")}
              {selectedTenancy && (
                <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" data-testid="report-tenancy-summary">
                  <div><dt className="text-slate-500">Tenant</dt><dd className="text-slate-900 font-medium">{selectedTenancy.tenant_name}</dd></div>
                  <div className="col-span-2"><dt className="text-slate-500">Property</dt><dd className="text-slate-900">{selectedTenancy.property_address}</dd></div>
                  <div><dt className="text-slate-500">Rent</dt><dd className="text-slate-900">{selectedTenancy.rent_amount ? `$${Number(selectedTenancy.rent_amount).toLocaleString()} / ${selectedTenancy.rent_frequency || 'month'}` : '—'}</dd></div>
                </dl>
              )}
            </>
          )}
        </Section>

        <Section title="Reporting period">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="period_start" className="text-sm text-slate-700">From</Label>
              <Input id="period_start" type="date" value={formData.period_start} onChange={(e) => handleChange('period_start', e.target.value)}
                className={`mt-1 ${inputCls} ${errors.period_start ? 'border-red-400' : ''}`} data-testid="report-period-start" />
              {err("period_start")}
            </div>
            <div>
              <Label htmlFor="period_end" className="text-sm text-slate-700">To</Label>
              <Input id="period_end" type="date" value={formData.period_end} onChange={(e) => handleChange('period_end', e.target.value)}
                className={`mt-1 ${inputCls} ${errors.period_end ? 'border-red-400' : ''}`} data-testid="report-period-end" />
              {err("period_end")}
            </div>
          </div>
        </Section>

        <Section title="Rent payment">
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" role="radiogroup" data-testid="report-payment-status">
            {PAYMENT_STATUSES.map((s, i) => {
              const on = formData.payment_status === s.value;
              return (
                <button key={s.value} type="button" role="radio" aria-checked={on} onClick={() => handleChange('payment_status', s.value)}
                  className={`px-3.5 h-9 text-sm transition-colors ${i > 0 ? 'border-l border-slate-300' : ''} ${on ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  data-testid={`payment-${s.value}`}>
                  {s.label}
                </button>
              );
            })}
          </div>

          {formData.payment_status !== 'on_time' && (
            <div className="grid sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
              <div>
                <Label htmlFor="days_late" className="text-sm text-slate-700">Days late</Label>
                <Input id="days_late" type="number" min="1" value={formData.days_late} onChange={(e) => handleChange('days_late', e.target.value)}
                  className={`mt-1 ${inputCls} ${errors.days_late ? 'border-red-400' : ''}`} data-testid="report-days-late" />
                {err("days_late")}
              </div>
              <div>
                <Label htmlFor="amount_owing" className="text-sm text-slate-700">Amount outstanding (CAD)</Label>
                <Input id="amount_owing" type="number" step="0.01" min="0" value={formData.amount_owing} onChange={(e) => handleChange('amount_owing', e.target.value)}
                  className={`mt-1 ${inputCls}`} data-testid="report-amount-owing" />
              </div>
            </div>
          )}
        </Section>

        <Section title="Lease">
          <div className="relative sm:w-64">
            <select value={formData.lease_status} onChange={(e) => handleChange('lease_status', e.target.value)} className={selectCls} data-testid="report-lease-status">
              {LEASE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          {formData.lease_status === 'broken' && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={formData.early_termination} onChange={(e) => handleChange('early_termination', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-teal-600" data-testid="report-early-termination" />
                Ended before the lease term
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={formData.notice_given} onChange={(e) => handleChange('notice_given', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-teal-600" data-testid="report-notice-given" />
                Required notice was given
              </label>
            </div>
          )}
        </Section>

        <Section title="Issues" action={
          <button type="button" onClick={addIssue} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1" data-testid="report-add-issue">
            <Plus className="w-3.5 h-3.5" /> Add issue
          </button>
        }>
          {formData.issues.length === 0 ? (
            <p className="text-sm text-slate-500">None recorded for this period.</p>
          ) : (
            <div className="divide-y divide-slate-100 -my-2">
              {formData.issues.map((issue, index) => (
                <div key={index} className="py-3 space-y-2" data-testid={`report-issue-${index}`}>
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <div className="relative">
                      <select value={issue.type} onChange={(e) => updateIssue(index, 'type', e.target.value)} className={selectCls}>
                        {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                    <div className="relative">
                      <select value={issue.severity} onChange={(e) => updateIssue(index, 'severity', e.target.value)} className={selectCls}>
                        {ISSUE_SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                    <button type="button" onClick={() => removeIssue(index)} className="h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" aria-label="Remove issue">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <Textarea value={issue.description} onChange={(e) => updateIssue(index, 'description', e.target.value)} rows={2}
                    className={`bg-white border-slate-300 text-slate-900 text-sm ${errors[`issue_${index}`] ? 'border-red-400' : ''}`} aria-label="Issue description" />
                  {err(`issue_${index}`)}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Notes">
          <Textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={3}
            className="bg-white border-slate-300 text-slate-900 text-sm" data-testid="report-notes" />
          <p className="text-xs text-slate-500 mt-1.5">Facts only. Notes appear verbatim on the tenant's Rent Report.</p>
        </Section>

        <div className="flex items-center justify-end gap-3">
          <a href="/landlord/reports" className="text-sm text-slate-600 hover:text-slate-900 px-3">Cancel</a>
          <Button type="submit" disabled={loading || tenancies.length === 0} className="h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm min-w-[120px]" data-testid="report-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'File report'}
          </Button>
        </div>
      </form>
    </div>
  );
}
